import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { QuestionMarkCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import React from "react";
import PrimaryButton from "./PrimaryButton";
import SecondaryButton from "./SecondaryButton";
import clsx from "clsx";
import { Tooltip } from "flowbite-react";

export default function Modal({
  open,
  onClose,
  onSubmit,
  disabled,
  children,
  title,
  description,
  startIcon,
  isSubmitting,
  submitLabel = "Submit",
  submitType = "submit",
  tooltip,
}) {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-[999999]">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/30 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-[999999] w-screen overflow-y-auto">
        <form
          className="flex min-h-full items-start justify-center sm:items-center"
          onSubmit={onSubmit}
        >
          <DialogPanel
            transition
            className="p-4 flex flex-col gap-4 sm:rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 w-full sm:max-w-lg max-sm:min-h-screen"
          >
            <div className="flex-1 flex flex-col gap-4">
              {/* Header */}
              <div
                className={clsx({
                  "border-b pb-2": Boolean(children),
                })}
              >
                <div
                  className={clsx(
                    "flex items-center gap-6 justify-between text-gray-500",
                    {
                      "mb-4": Boolean(startIcon),
                    }
                  )}
                >
                  <div>
                    {startIcon ? (
                      startIcon
                    ) : (
                      <DialogTitle
                        as="h3"
                        className="text-base font-semibold text-gray-900"
                      >
                        {title}
                      </DialogTitle>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={onClose}
                    className="hover:text-gray-900 p-2"
                  >
                    <XMarkIcon className="size-6" />
                  </button>
                </div>

                <div>
                  {Boolean(title && startIcon) && (
                    <DialogTitle
                      as="h3"
                      className="text-base font-semibold text-gray-900 flex items-center gap-2"
                    >
                      <span>{title}</span>
                      {open && tooltip && (
                        <Tooltip
                          theme={{
                            target: "flex",
                          }}
                          content={tooltip}
                        >
                          <QuestionMarkCircleIcon className="size-5 cursor-pointer" />
                        </Tooltip>
                      )}
                    </DialogTitle>
                  )}

                  {Boolean(description) && (
                    <p className="text-sm text-gray-500 p-0 m-0">
                      {description}
                    </p>
                  )}
                </div>
              </div>

              {/* Content */}
              {Boolean(children) && <div>{children}</div>}
            </div>

            {/* Sticky footer */}
            <div
              className={clsx(
                "sticky bottom-0 left-0 bg-white pb-4 -mb-4 z-10 border-gray-200 flex gap-4 max-sm:flex-col-reverse",
                {
                  "border-t pt-4": Boolean(children),
                }
              )}
            >
              <SecondaryButton type="button" onClick={onClose}>
                Cancel
              </SecondaryButton>
              <PrimaryButton
                onClick={submitType === "submit" ? undefined : onSubmit}
                type={submitType}
                disabled={disabled}
                loading={isSubmitting}
              >
                {submitLabel}
              </PrimaryButton>
            </div>
          </DialogPanel>
        </form>
      </div>
    </Dialog>
  );
}
